import { observer } from 'mobx-react';
import { logUsage } from 'owa-analytics';
import { logMountingPerformance } from 'owa-analytics';
import {
    ToggleFavoriteButton,
    lazyRemoveFavoritePrivateDistributionList,
    lazyAddFavoritePrivateDistributionList,
    isPrivateDLInFavorites,
    getFavoriteIdForPDL,
    isOutlookFavoritingInProgress,
} from 'owa-favorites';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { lazyNewMessage } from 'owa-mail-message-actions';
import { ActionSource } from 'owa-mail-store';
import * as React from 'react';
import { loadSubjectPickerModule } from 'owa-mail-subject-picker';
import HeaderActions from './../HeaderActions/HeaderActions';
import {
    personaCardBehavior,
    PersonaCardBehaviorProps,
} from 'owa-persona/lib/components/PersonaCardBehavior';
import MultiPersonaControl from 'owa-persona/lib/components/MultiPersonaControl/MultiPersonaControl';
import { PrivateDistributionListMember } from 'owa-persona';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { ControlIcons } from 'owa-control-icons';
import peopleStrings from 'owa-strings/owa-people-strings';
import { HeaderButtonData } from 'owa-people-persona-header-view';
import { format } from 'owa-strings';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./PersonaHeader.scss');

export interface MultiPersonaHeaderProps {
    displayName: string;
    personas: PrivateDistributionListMember[];
    pdlId?: string;
    owsPersonaId?: string;

    favoriteActionSource: ActionSource;

    hideFilter?: boolean;
    hideCheckAll?: boolean;
}

@logMountingPerformance('MultiPersonaHeader')
@observer
export default class MultiPersonaHeader extends React.Component<MultiPersonaHeaderProps, {}> {
    render() {
        const membersCount = this.props.personas.length;
        const multiMemberString = format(
            peopleStrings.multiPersonaHeaderContactListMultiMembersLabel,
            membersCount
        );
        const pdlSubtitle = `${peopleStrings.multiPersonaHeaderContactListLabel} • ${
            membersCount === 1
                ? peopleStrings.multiPersonaHeaderContactListSingleMemberLabel
                : multiMemberString
        }`;

        const subtitle = this.props.pdlId
            ? pdlSubtitle
            : format(peopleStrings.multiPersonaHeaderSearchMultiPeopleLabel, membersCount);

        const tableView = getSelectedTableView();
        const isEVHeader = isFeatureEnabled('peo-ev-header');

        return (
            <div className={styles.headerContainer}>
                <div className={styles.imageAndInfoContainer}>
                    {this.wrapInLPCTarget(
                        <MultiPersonaControl personas={this.props.personas} isSearchPill={false} />,
                        false /*isHiddenOverflow*/
                    )}
                    <div className={styles.personInfoContainer}>
                        <div className={styles.firstLine}>
                            {this.wrapInLPCTarget(
                                <div className={styles.personName}>{this.props.displayName}</div>,
                                true /*isHiddenOverflow*/
                            )}
                            <div className={styles.favoriteContainer}>
                                <ToggleFavoriteButton
                                    isInFavorites={this.isFavorite()}
                                    buttonStyles={styles.favoriteButton}
                                    iconStyles={styles.favoriteIcon}
                                    onClick={this.toggleFavoriteState}
                                    animate={true}
                                />
                            </div>
                        </div>
                        <div className={styles.personEmail}>{subtitle}</div>
                    </div>
                </div>
                <HeaderActions
                    tableView={tableView}
                    hideCheckAll={this.props.hideCheckAll}
                    hideFilter={this.props.hideFilter}
                    actionButtonData={this.getActionButtons()}
                    isEVHeader={isEVHeader}
                />
            </div>
        );
    }

    componentDidMount(): void {
        logUsage('MultiPersonaHeaderRendered', [this.props.favoriteActionSource]);
    }

    componentDidUpdate(prevProps: MultiPersonaHeaderProps): void {
        if (
            this.props.pdlId !== prevProps.pdlId ||
            this.props.personas.length !== prevProps.personas.length ||
            this.props.favoriteActionSource !== prevProps.favoriteActionSource
        ) {
            logUsage('MultiPersonaHeaderRendered', [this.props.favoriteActionSource]);
        }
    }

    private getActionButtons(): HeaderButtonData[] {
        return [
            {
                icon: ControlIcons.Mail,
                text: peopleStrings.emailButtonLabel,
                clickCallback: this.handleEmailClick,
            },
        ];
    }

    private handleEmailClick = async () => {
        logUsage('MailComposeFromHeader');

        // We need to load module to initialize all the orchestrators and mutators
        await loadSubjectPickerModule();

        if (this.props.pdlId) {
            const recipient: EmailAddressWrapper = {
                ItemId: {
                    Id: this.props.pdlId,
                },
                Name: this.props.displayName,
                MailboxType: 'PrivateDL',
            };
            lazyNewMessage.importAndExecute(this.props.favoriteActionSource, null /* groupId */, [
                recipient,
            ]);
        } else {
            // From search
            lazyNewMessage.importAndExecute(
                this.props.favoriteActionSource,
                null /* groupId */,
                this.props.personas.map(persona => ({
                    EmailAddress: persona.emailAddress,
                    Name: persona.name,
                }))
            );
        }
    };

    private toggleFavoriteState = async (evt: React.MouseEvent<EventTarget>) => {
        evt.stopPropagation();
        if (isOutlookFavoritingInProgress('FavoritePdl')) {
            // Ignore add/remove of any favorite if another PDL favoriting is in progress.
            return;
        }

        if (!this.isFavorite()) {
            const addFavoritePrivateDistributionList = await lazyAddFavoritePrivateDistributionList.import();
            addFavoritePrivateDistributionList(
                this.props.displayName,
                this.props.personas,
                this.props.pdlId,
                this.props.owsPersonaId,
                this.props.favoriteActionSource
            );
        } else {
            const removeFavoritePrivateDistributionList = await lazyRemoveFavoritePrivateDistributionList.import();

            const favoriteId = getFavoriteIdForPDL(this.props.pdlId, this.props.personas);

            removeFavoritePrivateDistributionList(favoriteId, this.props.favoriteActionSource);
        }
    };

    private isFavorite(): boolean {
        return isPrivateDLInFavorites(this.props.pdlId, this.props.personas);
    }

    private wrapInLPCTarget(children: React.ReactNode, isHiddenOverflow: boolean): JSX.Element {
        if (!this.props.pdlId) {
            // don't render LPC from multi-persona search scenario
            return <> {children} </>;
        }

        return (
            <LPCWrapper
                emailAddress={this.props.pdlId}
                olsPersonaId={this.props.pdlId}
                disableHover={true}
                name={this.props.displayName}
                personaType="PrivateDL"
                mailBoxType="PrivateDL"
                composeEmailOverride={this.handleEmailClick}
                className={isHiddenOverflow ? styles.hiddenOverflow : undefined}
                clientScenario="MultiPersonaHeader">
                {children}
            </LPCWrapper>
        );
    }
}

@personaCardBehavior
class LPCWrapper extends React.Component<PersonaCardBehaviorProps, React.ComponentState> {
    render(): any {
        return (
            <div
                onClick={this.props.onPersonaCardBehaviorClick}
                onMouseEnter={this.props.onPersonaCardBehaviorMouseEnter}
                onMouseLeave={this.props.onPersonaCardBehaviorMouseLeave}>
                {this.props.children}
            </div>
        );
    }
}
